import { Flex, Steps, Text, AppLoader, Button } from '@components';
import { Box, useTheme } from '@mui/material';
import WithUser from '@HOCs/withUser';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './UltimateBeneficialOwners.styles';
import { WithUserProps } from 'HOCs/withUser';
import React, { useEffect, useState } from 'react';
import { InvestmentContainer } from '../Investment.styles';
import { LEGAL_ENTITY_TOTAL_STEPS } from '../Investment.consts';
import { useUltimateBeneficialOwnersQuery } from '@features/api/ultimateBeneficialOwnerSlice';
import { UltimateBeneficialOwner } from '@features/api/response/UltimateBeneficialOwner';
import UltimateBeneficialOwnerForm from './UltimateBeneficialOwnerForm/UltimateBeneficialOwnerForm';

const UltimateBeneficialOwnersPage: React.FC<
  WithUserProps & { ultimateBeneficialOwners?: UltimateBeneficialOwner[] }
> = ({ user, ultimateBeneficialOwners }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [ubos, setUbos] = useState<Partial<UltimateBeneficialOwner>[]>(
    ultimateBeneficialOwners?.length ? [...ultimateBeneficialOwners] : [{}]
  );
  const [activeUboIndex, setActiveUboIndex] = useState<number | undefined>(0);

  useEffect(() => {
    setUbos(
      ultimateBeneficialOwners?.length ? [...ultimateBeneficialOwners] : [{}]
    );
    setActiveUboIndex(ultimateBeneficialOwners?.length ? undefined : 0);
  }, [ultimateBeneficialOwners]);

  const onSubmit = async () => {
    try {
      if (!ultimateBeneficialOwners?.length) return;

      navigate(`/investment-subscription/${productId}/authorized-signatories`, {
        state: {
          progress: 5,
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddUbo = () => {
    setUbos([...ubos, {}]);
    setActiveUboIndex(ubos.length);
  };

  const handleSaveUbo = () => {
    setActiveUboIndex(undefined);
  };

  const handleDeleteUbo = () => {
    setActiveUboIndex(!ubos.length ? undefined : 0);
  };

  const handleRemoveUbo = () => {
    setActiveUboIndex(undefined);
    setUbos(
      ultimateBeneficialOwners?.length ? [...ultimateBeneficialOwners] : [{}]
    );
  };

  const handleEditUbo = (index: number) => {
    setActiveUboIndex(index);
  };

  if (!user) return <></>;

  return (
    <InvestmentContainer data-testid='ultimate-beneficial-owners-container'>
      <ContentContainer>
        <Steps currentStep={2} totalSteps={LEGAL_ENTITY_TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.ubo.uboTitle' />
        </Text>
        <Text
          variant='h6'
          mt={5}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          <FormattedMessage id='investment.ubo.uboSubtitle' />
        </Text>

        <Flex flex={1} mt={16} flexDirection='column' gap={12}>
          {ubos.map((x, i) => (
            <UltimateBeneficialOwnerForm
              ultimateBeneficialOwner={x}
              isActive={i === activeUboIndex}
              onSave={handleSaveUbo}
              onDelete={handleDeleteUbo}
              onRemove={handleRemoveUbo}
              onEdit={() => handleEditUbo(i)}
              canRemove={ubos.length > 1}
              showDivider={i > 0}
            />
          ))}
          {activeUboIndex === undefined ? (
            <Box alignSelf='center'>
              <Button variant='tertiary' onClick={handleAddUbo}>
                <FormattedMessage id='investment.ubo.addAnotherUbo' />
              </Button>
            </Box>
          ) : null}
        </Flex>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/company`, {
              state: {
                progress: 2,
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !ultimateBeneficialOwners?.length,
          onClick: onSubmit,
        }}
      />
    </InvestmentContainer>
  );
};

const CompanyWithProps: React.FC<WithUserProps> = ({ user, refetchUser }) => {
  const { data: ultimateBeneficialOwners, isLoading } =
    useUltimateBeneficialOwnersQuery();

  if (isLoading) return <AppLoader />;

  return (
    <UltimateBeneficialOwnersPage
      user={user}
      refetchUser={refetchUser}
      ultimateBeneficialOwners={ultimateBeneficialOwners}
    />
  );
};

export default WithUser(CompanyWithProps);
